import {
  Centered,
  EmptyState,
  FlexColumn,
  FlexRow,
  ImageThumbnail,
  Loading,
  useLoading,
  useMoment,
  VideoThumbnail,
} from '@gorila-shared-ui/components';
import { Cell, Grid } from 'baseui/layout-grid';
import { HeadingXSmall, LabelMedium, LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FILETYPE_PICTURE, FILETYPE_VIDEO } from '../../constants/app';
import { useStyles } from '../../hooks/useStyles';
import { getEventAlarmEvidenceUrl } from '../../services/gptService';
import { eventDetailState } from '../../storage/gpt';
import { EventAlarm } from '../../types/gpt';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { useGptEventAlarmTypes } from './hooks/useGptEventAlarmTypes';
import GptEventEvicencesPreviewModal from './modal/GptEventEvicencesPreviewModal';

function GptEventEvidences() {
  const { theme, css, classes } = useStyles();
  const { getAlarmTypeName } = useGptEventAlarmTypes();
  const event = useRecoilValue(eventDetailState);
  const { loading, startLoading, stopLoading } = useLoading();
  const [loadedAlarmsEvidences, setLoadedAlarmsEvidences] = useState<EventAlarm[]>([]);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number>();
  const { formatDateFromTs } = useMoment();
  const alarms = event?.alarms ?? [];
  const evidenceFiles = useMemo(() => {
    return loadedAlarmsEvidences.flatMap((alarm) => alarm.evidences);
  }, [loadedAlarmsEvidences]);

  useEffect(() => {
    const loadEvidencesUrl = () => {
      if (alarms.length) {
        startLoading();
        const newAlarmEvidences: EventAlarm[] = [];
        alarms.forEach(async (alarm) => {
          const evidences = alarm.evidences ?? [];
          if (evidences.length) {
            const newEvidences = [...evidences];
            const requests = evidences.map((evidence, index) => {
              return getEventAlarmEvidenceUrl(evidence.key).then((result) => ({ ...result, index }));
            });
            const results = await Promise.allSettled(requests);
            results.forEach((result) => {
              if (result.status === 'fulfilled') {
                const index = result.value.index;
                const url = result.value.url ?? '';
                const evidence = newEvidences[index];
                newEvidences[index] = {
                  ...evidence,
                  url,
                };
              }
            });
            newAlarmEvidences.push({
              ...alarm,
              evidences: newEvidences,
            });
          }
        });
        setLoadedAlarmsEvidences(newAlarmEvidences);
        stopLoading();
      }
    };

    loadEvidencesUrl();
  }, [alarms]);

  if (!event || !alarms.length) return null;

  return (
    <FloatingCardContent flexible gap={theme.sizing.scale600}>
      <FlexColumn
        classNames={css({
          overflowY: 'hidden',
          height: '100%',
        })}
      >
        <FlexRow gap={theme.sizing.scale200} classNames={classes.centeredStart}>
          <HeadingXSmall margin={0}>Evidencias</HeadingXSmall>
        </FlexRow>
        {loading && <Loading />}
        {!loading && !!loadedAlarmsEvidences.length && (
          <FlexColumn classNames={css({ overflowY: 'auto' })} gap={0}>
            {loadedAlarmsEvidences.map((alarm, index) => {
              return (
                <FlexColumn key={index} gap={theme.sizing.scale300}>
                  <FlexRow classNames={`${classes.dataHeaderClass} ${css({ alignItems: 'baseline' })}`}>
                    <LabelMedium>{getAlarmTypeName(alarm.alarmType)}</LabelMedium>
                    <LabelSmall className={css({ textDecoration: 'underline' })}>
                      {`
                        ${alarm.gpsSpeed || 0} Km/h, 
                        ${alarm.startTime ? formatDateFromTs(alarm.startTime) : ''}
                      `}
                    </LabelSmall>
                  </FlexRow>
                  <Grid
                    gridColumns={12}
                    gridGaps={[8]}
                    gridGutters={[16]}
                    gridMargins={[0]}
                    behavior="fluid"
                    overrides={{
                      GridWrapper: {
                        style: {
                          width: '100%',
                          margin: 0,
                          overflowX: 'hidden',
                        },
                      },
                    }}
                  >
                    {alarm.evidences.map((evidenceFile, index) => {
                      if (!evidenceFile.url) return null;

                      const imageIndex = evidenceFiles.findIndex((file) => file.key === evidenceFile.key);
                      return (
                        <Cell span={[12, 12, 12, 12, 6]} key={index}>
                          {evidenceFile.fileType === FILETYPE_PICTURE && (
                            <ImageThumbnail
                              url={evidenceFile.url}
                              onClick={() => setSelectedEvidenceIndex(imageIndex)}
                            />
                          )}
                          {evidenceFile.fileType === FILETYPE_VIDEO && (
                            <VideoThumbnail
                              onClick={() => setSelectedEvidenceIndex(imageIndex)}
                              url={evidenceFile.url}
                            />
                          )}
                        </Cell>
                      );
                    })}
                  </Grid>
                </FlexColumn>
              );
            })}
          </FlexColumn>
        )}
        {!loading && !loadedAlarmsEvidences.length && (
          <Centered>
            <EmptyState title="No se encontraron evidencias." />
          </Centered>
        )}
      </FlexColumn>
      {selectedEvidenceIndex !== undefined && (
        <GptEventEvicencesPreviewModal
          isOpen
          onClose={() => setSelectedEvidenceIndex(undefined)}
          evidenceFiles={evidenceFiles}
          selectedItemIndex={selectedEvidenceIndex}
        />
      )}
    </FloatingCardContent>
  );
}

export default GptEventEvidences;
